import type React from "react";

import { Box } from "@/design-system/Box";
import { Stack } from "@/design-system/Stack";
import { useTranslation } from "@/translations";

import { AskNobiChatMessage } from "./AskNobiChatMessage";
import { AskNobiChatMessageThinking } from "./AskNobiChatMessageThinking";
import { AskNobiSuggestionButton } from "./AskNobiSuggestionButton";
import type { UseAskNobiChatResult } from "./helpers/useAskNobiChat";

interface AskNobiChatFeedProps {
  chat: UseAskNobiChatResult;
  onSelectFollowUp: (suggestion: string) => void;
}

export function AskNobiChatFeed({ chat, onSelectFollowUp }: AskNobiChatFeedProps): React.ReactNode {
  const { t } = useTranslation();
  const visibleMessages = [...chat.messages].reverse();

  // we group messages by sender so we can do proper spacing
  const messageGroups = visibleMessages.reduce(
    (groups, message) => {
      const lastGroup = groups[groups.length - 1];
      if (lastGroup && lastGroup.sender === message.sender) {
        lastGroup.messages.push(message);
      } else {
        groups.push({ sender: message.sender, messages: [message] });
      }

      return groups;
    },
    [] as { sender: (typeof visibleMessages)[number]["sender"]; messages: typeof visibleMessages }[],
  );

  const lastGroupIsAssistant = messageGroups[0]?.sender === "assistant";
  const hasActiveActivity = chat.messages.some(
    (message) => message.type === "activity" && message.status === "running",
  );
  const latestMessage = chat.messages.at(-1);
  const followUpSuggestions =
    chat.state === "idle" && latestMessage?.type === "text" && latestMessage.sender === "assistant"
      ? latestMessage.followUpSuggestions
      : undefined;

  return (
    <section className="mx-auto flex w-full max-w-sm flex-col gap-space-md">
      <ol className="flex flex-col-reverse gap-space-md">
        {!hasActiveActivity &&
          !lastGroupIsAssistant &&
          (chat.state === "connecting" || chat.state === "preparing-to-chat") && (
            <AskNobiChatMessageThinking connecting={chat.state === "connecting"} pipelineStage={chat.pipelineStage} />
          )}
        {messageGroups.map((group, index) => (
          <li className="flex flex-col-reverse" key={index}>
            {group.messages.map((msg) => (
              <AskNobiChatMessage key={`${msg.type}-${msg.id}`} message={msg} conversationId={chat.conversationId} />
            ))}
          </li>
        ))}
      </ol>
      {!hasActiveActivity &&
        lastGroupIsAssistant &&
        (chat.state === "connecting" || chat.state === "preparing-to-chat") && (
          <AskNobiChatMessageThinking connecting={chat.state === "connecting"} pipelineStage={chat.pipelineStage} />
        )}
      {followUpSuggestions && followUpSuggestions.length > 0 && (
        <Box padding={{ bottom: "md" }}>
          <Stack as="nav" gap="sm" aria-label={t("component.ask-nobi-chat-drawer.title")}>
            {followUpSuggestions.map((suggestion) => (
              <AskNobiSuggestionButton key={suggestion} onClick={() => onSelectFollowUp(suggestion)}>
                {suggestion}
              </AskNobiSuggestionButton>
            ))}
          </Stack>
        </Box>
      )}
    </section>
  );
}
